/**
 * Ingame Navigation Component for Campaigns
 * Bottom tab navigation for campaign single-view mode
 */

import React, { useCallback, useMemo, memo } from 'react';
import { INGAME_CAMPAIGN_TABS } from '@repo/common';
import IngameNavigationItem from './IngameNavigationItem/IngameNavigationItem';
import { useCampaignSectionGate } from '../../../hooks/useCampaignSectionGate';
import { useHideNavFooterOnScroollMobile } from 'shared/hooks/useHideNavFooterOnScroollMobile';

import styles from './IngameNavigation.module.css';

interface IngameNavigationProps {
  activeTab: keyof typeof INGAME_CAMPAIGN_TABS,
  onTabChange: (tabName: keyof typeof INGAME_CAMPAIGN_TABS) => void;
}

const IngameCampaignNavigation: React.FC<IngameNavigationProps> = ({ activeTab, onTabChange }) => {
  const { isCampaignEnabled } = useCampaignSectionGate();
  const isHidden = useHideNavFooterOnScroollMobile();

  const handleTabClick = useCallback((tabName: keyof typeof INGAME_CAMPAIGN_TABS) => {
    onTabChange(tabName);
  }, [onTabChange]);

  const visibleTabs = useMemo(
    () =>
      (Object.keys(INGAME_CAMPAIGN_TABS) as Array<keyof typeof INGAME_CAMPAIGN_TABS>).filter(
        (tabName) => isCampaignEnabled(INGAME_CAMPAIGN_TABS[tabName]) !== false,
      ),
    [isCampaignEnabled],
  );

  // Don't show navigation if there is only one or no available tabs
  if(visibleTabs.length <= 1) return;

  return (
    <div className={styles.navigationContainer} data-hide={isHidden}>
      <div className={styles.backdrop} />
      <div className={styles.tabsWrapper}>
        {visibleTabs.map((tabName) => (
          <IngameNavigationItem
            key={tabName}
            tabName={tabName}
            isActive={activeTab === tabName}
            onClick={handleTabClick}
            />
        ))}
      </div>
    </div>
  );
};

export default memo(IngameCampaignNavigation);
